import React, {useReducer} from 'react';
import {CheckIcon, XMarkIcon} from '@heroicons/react/24/outline';
import type {PlanType} from 'api/generated';
import {Button, ButtonClipboard, Modal} from 'components';
import {usePersistentStore} from 'stores';
import {classNames} from 'utils';

import {type PayDurationType} from './Plans.Toggle.PayDuration';
import usePlanTable from '../../hooks/usePlanTable';

interface IPlansTableDesktopProps {
  payDuration: PayDurationType;
  setPayDuration: (payDuration: PayDurationType) => void;
  plans: PlanType[];
  currentPlan: PlanType;
  onSubscribe: (plan: PlanType) => void;
}

const PlansTableDesktop = ({plans, currentPlan, onSubscribe}: IPlansTableDesktopProps) => {
  const [showModal, toggleModal] = useReducer(s => !s, false);
  const countryCode = usePersistentStore(s => s.countryCode.code);
  const {plansWithMine, features, showFeatureOverride, toggleFeatureOverride, shouldHighlight, getFeatureKey} = usePlanTable({plans, currentPlan});
  const cols = {3: 'grid-cols-4', 4: 'grid-cols-5'}[plans.length] ?? 'grid-cols-3';
  return (
    <section className="hidden lg:block pb-6">
      {/* <div className="pb-6">
        <PlansTogglePayDuration payDuration={payDuration} setPayDuration={setPayDuration} />
      </div> */}
      {/* Headers */}
      <div className={classNames('grid gap-x-8 before:block pb-4', cols)}>
        {plansWithMine.map(plan => {
          return (
            <div key={plan.uid} aria-hidden="true" className="-mt-px text-center">
              <h3 className={classNames('text-xl font-semibold', currentPlan.uid === plan.uid ? 'text-primary' : 'text-gray-900 dark:text-white')}>
                {plan.name}
              </h3>
              <p className="mt-1 text-sm/6 text-gray-600 dark:text-darkText">{plan.description}</p>
            </div>
          );
        })}
      </div>
      {/* Prices */}
      <div className={classNames('grid gap-x-8 before:block pb-5', cols)}>
        {plansWithMine.map(plan => {
          if (plan.billing.custom) {
            return (
              <div key={plan.uid} className="flex flex-col items-center text-center">
                <div className="flex items-center mb-1 text-slate-900 dark:text-white">
                  <span className="text-4xl">Custom</span>
                </div>
              </div>
            );
          }
          return (
            <div key={plan.uid} className="flex flex-col items-center text-center">
              <div className="flex items-center mb-1 text-slate-900 dark:text-white">
                {/* <span className="text-4xl">{plan.billing.symbol}</span> */}
                {/* TODO: implement different currency symbol and price */}
                <span className="text-4xl">{countryCode === 'MY' ? 'RM' : '$'}</span>
                <span className="text-4xl">{plan.billing.priceCents / 100}</span>
              </div>
              <span className="text-slate-900 dark:text-white leading-6 lowercase text-sm">
                {/* {plan.billing.currency} / {plan.billing.interval} */}
                {/* TODO: implement different currency symbol and price */}
                {countryCode === 'MY' ? 'myr' : 'usd'} / {plan.billing.interval}
              </span>
            </div>
          );
        })}
      </div>
      {/* CTA */}
      <div className={classNames('grid gap-x-8 before:block pb-6', cols)}>
        {plansWithMine.map(plan => {
          if (currentPlan.uid === plan.uid) {
            return (
              <Button
                key={plan.uid}
                text={showFeatureOverride ? 'Current' : 'Default'}
                size="small"
                rounded
                onClick={toggleFeatureOverride}
                variant="secondary"
                disabled={!currentPlan.featuresOverride}
              />
            );
          }
          if (plan.billing.custom) {
            return <Button key={plan.uid} text="Contact Us" size="small" rounded onClick={toggleModal} />;
          }
          {
            const isUpgrade = currentPlan.sequence < plan.sequence;
            const text = isUpgrade ? 'Upgrade' : 'Downgrade';
            const variant = isUpgrade ? 'primary' : 'secondary';
            return (
              <Button key={plan.uid} text={text} size="small" variant={variant} rounded disabled={!isUpgrade} onClick={() => onSubscribe(plan)} />
            );
          }
        })}
      </div>
      {/* Features Listing */}
      <div className="-mt-3">
        <h4 className="text-sm/6 font-semibold text-gray-900 dark:text-white">Features</h4>
        <div className="relative -mx-8 mt-3">
          {/* Fake card backgrounds */}
          <div aria-hidden="true" className={classNames('absolute inset-x-8 inset-y-0 grid gap-x-8 before:block', cols)}>
            {Array(plans.length)
              .fill(null)
              .map((_, idx) => {
                return <div key={idx} className="size-full rounded-lg bg-white shadow-xs dark:bg-slate-800/50 dark:shadow-none" />;
              })}
          </div>
          <table className="relative w-full border-separate border-spacing-x-8">
            <thead>
              <tr className="text-left">
                <th scope="col">
                  <span className="sr-only">Feature</span>
                </th>
                {plansWithMine.map(plan => (
                  <th key={plan.uid} scope="col">
                    <span className="sr-only">{plan.name} tier</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature, featureIdx) => (
                <tr key={feature.name}>
                  <th scope="row" className="w-1/4 py-3 pr-4 text-left text-sm/6 font-normal text-gray-900 dark:text-white">
                    {feature.name}
                    {featureIdx !== features.length - 1 ? <div className="absolute inset-x-8 mt-3 h-px bg-gray-200 dark:bg-white/10" /> : null}
                  </th>
                  {plansWithMine.map(plan => {
                    const featureKey = getFeatureKey(plan);
                    return (
                      <td key={plan.uid} className="relative w-1/4 px-4 py-0 text-center">
                        <span className="relative size-full py-3">
                          {typeof feature.plans[plan.name][featureKey] === 'string' ? (
                            <span
                              className={classNames(
                                'text-sm/6',
                                shouldHighlight(plan) ? 'font-semibold text-primary' : 'text-gray-900 dark:text-white',
                              )}>
                              {feature.plans[plan.name][featureKey]}
                            </span>
                          ) : (
                            <>
                              <span className="sr-only">{feature.plans[plan.name][featureKey] === true ? 'Yes' : 'No'}</span>
                              {feature.plans[plan.name][featureKey] === true ? (
                                <CheckIcon
                                  aria-hidden="true"
                                  className={classNames('mx-auto size-5', shouldHighlight(plan) ? 'text-primary' : 'text-gray-800 dark:text-white')}
                                />
                              ) : (
                                <XMarkIcon aria-hidden="true" className="mx-auto size-5 text-gray-400 dark:text-darkText/50" />
                              )}
                            </>
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Fake card borders */}
          <div aria-hidden="true" className={classNames('pointer-events-none absolute inset-x-8 inset-y-0 grid gap-x-8 before:block', cols)}>
            {plansWithMine.map(plan => {
              return (
                <div
                  key={plan.uid}
                  className={classNames(shouldHighlight(plan) ? 'ring-2 ring-primary' : 'ring-1 ring-gray-900/10 dark:ring-white/10', 'rounded-lg')}
                />
              );
            })}
          </div>
        </div>
      </div>
      <Modal visible={showModal} onClose={toggleModal}>
        <h2 className="text-2xl dark:text-white mb-5">Contact us</h2>
        <div className="flex items-center gap-x-3 text-primary">
          <p className="text-slate-800 dark:text-darkText">Email:</p>
          <a href="mailto:admin@das-ar.com" className="font-semibold underline sm:no-underline hover:underline">
            admin@das-ar.com
          </a>
          <ButtonClipboard text="admin@das-ar.com" />
        </div>
      </Modal>
    </section>
  );
};

export default React.memo(PlansTableDesktop);
